'use client';

import { parseAsString, useQueryStates } from 'nuqs';
import { useEffect, useState } from 'react';

import { AuthErrorLogger } from '../AuthErrorLogger';
import { AuthBackground, AuthCard } from '../CommonStyles/AuthLayout.styles';

import type { ReactNode } from 'react';

export type AuthErrorState = {
  code: string;
  detail: string;
  message: string;
};

type AuthErrorQueryParams = {
  error?: string | null;
  error_description?: string | null;
};

export const getAuthErrorState = (
  authErrorParams?: AuthErrorQueryParams,
  fallbackMessage = 'There is an issue with SSO. Please try again later or contact support if the problem continues.',
): AuthErrorState | null => {
  if (!authErrorParams) {
    return null;
  }

  const rawError = authErrorParams.error;
  const rawDetail = authErrorParams.error_description ?? authErrorParams.error;

  if (!rawError && !rawDetail) {
    return null;
  }

  const errorCode = typeof rawError === 'string' ? rawError : '';
  const detail = typeof rawDetail === 'string' ? rawDetail : '';

  if (!errorCode && !detail) {
    return null;
  }

  const normalizedCode = errorCode || 'signin_oauth_error';
  const normalizedDetail = detail || 'The sign-in provider returned an error.';

  return {
    code: normalizedCode,
    detail: normalizedDetail,
    message: fallbackMessage,
  };
};

export type AuthLayoutAuthError = AuthErrorState;

type AuthLayoutProps = {
  authError?: AuthLayoutAuthError | null;
  authErrorMessage?: string;
  children: ReactNode;
  notice?: ReactNode;
};

export const AuthLayout = ({ authError = null, authErrorMessage, children, notice }: AuthLayoutProps) => {
  const [resolvedAuthError, setResolvedAuthError] = useState<AuthLayoutAuthError | null>(authError ?? null);
  const [{ error: errorCode, error_description: errorDescription }] = useQueryStates({
    error: parseAsString,
    error_description: parseAsString,
  });

  useEffect(() => {
    if (authError) {
      setResolvedAuthError(authError);

      return;
    }

    const nextAuthError = getAuthErrorState(
      {
        error: errorCode ?? undefined,
        error_description: errorDescription ?? undefined,
      },
      authErrorMessage,
    );

    setResolvedAuthError(nextAuthError);
  }, [authError, authErrorMessage, errorCode, errorDescription]);

  const renderedNotice = notice ?? <AuthErrorLogger initialAuthError={resolvedAuthError ?? null} />;

  return (
    <AuthBackground>
      {renderedNotice ? (
        <div style={{ marginBottom: '1rem', width: '100%', display: 'flex', justifyContent: 'center' }}>
          {renderedNotice}
        </div>
      ) : null}
      <AuthCard>{children}</AuthCard>
    </AuthBackground>
  );
};
